import React, { useState, useEffect } from 'react'
import { connect, useSelector } from 'react-redux'
import deepEqual from 'deep-equal'
import { cloneDeep } from 'lodash'
import BackIcon from '@material-ui/icons/KeyboardBackspace';
import { Button, Grid, makeStyles, Paper, Tab, Tabs, TextField } from '@material-ui/core'

import Loader from '../../../common/components/notifications/Loader'
import queryString from '../../../utils/QueryString'
import CategorySelect from '../../../embed/hoc/types/CategorySelect'
import GroupUsers from './GroupUsers';
import { getHasRequestPending } from '../../../redux/data/selectors';
import { CREATE_CONTENT_GROUP } from '../../../redux/data/types';
import RestrictedComponent, { hasPermission } from '../../../lib/components/form/RestrictedComponent';
import schema from '../../../routes/permissions/schema';

const useStyles = makeStyles(theme => ({
  backButton: {
    color: 'gray'
  },
  settingForm: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    paddingTop: 0,
  },
  settingFormText: {
    marginTop: '1rem'
  },
  tabs: {
    paddingBottom: '1rem'
  },
  actionButton: {
    margin: '1rem'
  },
  categorySelectContainer: {
    margin: '1rem'
  }
}))

const Settings = props => {
  const { contentGroup, onChange } = props
  const classes = useStyles()

  const clientCategories = useSelector(state => state.data.categories)

  const handleChange = (propertyKey, propertySubKey, newValue) => {
    const newContentGroup = cloneDeep(contentGroup)
    if (propertySubKey) {
      newContentGroup[propertyKey][propertySubKey] = newValue
    } else {
      newContentGroup[propertyKey] = newValue
    }
    onChange(newContentGroup)
  }

  const handleSelectCategories = selectedCategoryNames => {
    const categories = selectedCategoryNames.map(name => {
      return clientCategories.find(clientCategory => {
        return clientCategory.categoryName == name
      })
    })
    handleChange('GroupCategories', undefined, categories)
  }

  return <Paper className={classes.settingForm}>
    <Grid container>
      <Grid item xs={12} sm={6} md={4}>
        <TextField
          fullWidth
          className={classes.settingFormText}
          value={contentGroup.Group.groupName}
          onChange={(ev) => handleChange('Group', 'groupName', ev.target.value)}
          label='Name'
          placeholder='Name'
          variant='outlined'
        />
        <TextField
          fullWidth
          className={classes.settingFormText}
          value={contentGroup.Group.groupDescription}
          onChange={(ev) => handleChange('Group', 'groupDescription', ev.target.value)}
          label='Description'
          placeholder='Description'
          variant='outlined'
          multiline
          rows={4}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={8}>
        <div className={classes.categorySelectContainer}>
          <CategorySelect
            value={contentGroup.GroupCategories.map(category => category.categoryName)}
            categories={clientCategories}
            storeEdits={selectedCategoryNames => handleSelectCategories(selectedCategoryNames)}
            clearFull={false}
            useName={true}
            useHidden={true}
            showAll={true}
          />
        </div>
      </Grid>
    </Grid>
  </Paper>

}

const GroupDetails = props => {
  const {
    contentGroup,
    pending,
    onSave,
    onClose,
    categoriesLoaded,
    requestCategories,
    id,
    assignUsers,
    removeUser,
    createPending
  } = props
  const [editedGroup, setEditedGroup] = useState(contentGroup)
  const [waitingForSave, setWaitingForSave] = useState(false)
  const classes = useStyles()

  useEffect(() => {
    if (createPending) {
      // started waiting for create request
      setWaitingForSave(true)
    } else {
      if (waitingForSave) {
        // was waiting for create request and it is finished now
        onClose()
      }
    }
  }, [createPending])

  useEffect(() => {
    if (!categoriesLoaded && !pending) {
      requestCategories()
    }
  }, [categoriesLoaded, pending])

  const handleSave = () => {
    onSave(editedGroup, contentGroup)
  }

  const handleClose = () => {
    const qs = queryString.parse(location.search)
    delete qs.contentGroup
    delete qs.tab
    queryString.set({ ...qs })
    onClose()
  }

  const qs = queryString.parse(location.search)
  let qsTab = parseInt(qs.tab)
  if (isNaN(qsTab) || qsTab === null || qsTab === undefined) {
    qsTab = 0
  }
  const [selectedTab, setSelectedTab] = useState(qsTab)

  // if the content group is updated reflect those changes on the edited group
  // this might want to be blocked by a confirmation dialog to the user if the want to use the
  // updated version or their own
  useEffect(() => {
    if (!deepEqual(contentGroup, editedGroup) && !!id) {
      setEditedGroup(cloneDeep(contentGroup))
    }
  }, [contentGroup])

  useEffect(() => {
    if (!deepEqual(contentGroup, editedGroup) && !id) {
      if (!deepEqual(contentGroup.GroupUsers, editedGroup.GroupUsers)) {
        const newGroup = cloneDeep(editedGroup)
        newGroup.GroupUsers = contentGroup.GroupUsers
        setEditedGroup(cloneDeep(newGroup))
      }
    }
  }, [contentGroup])

  if (pending) return <Loader />
  if (!contentGroup) {
    handleClose()
  }

  let renderTab
  const edited = !deepEqual(contentGroup, editedGroup)
  switch (selectedTab) {
    case 0:
      renderTab = (
        <>
          <Settings
            contentGroup={editedGroup}
            onChange={(edited) => {
              setEditedGroup(edited)
            }}
          />
          <Button
            className={classes.actionButton}
            onClick={() => setEditedGroup(contentGroup)}
            variant='contained'
            disabled={!edited}
          >
            Reset
          </Button>
          <RestrictedComponent
            permissionCheck={
              contentGroup.Group.groupID
                ? schema.ACCOUNT_CONTENT_GROUPS.UPDATE
                : schema.ACCOUNT_CONTENT_GROUPS.CREATE
            }
          >
            <Button
              className={classes.actionButton}
              onClick={handleSave}
              variant='contained'
              color='primary'
              disabled={!edited}
            >
              Save
            </Button>
          </RestrictedComponent>
        </>
      )
      break
    case 1:
      renderTab = <GroupUsers
        contentGroup={editedGroup}
        onAssign={assignUsers}
        onRemove={removeUser}
      />
      break
  }

  if (!editedGroup) return <div>The selected content group does not exist! Redirecting...</div>
  return <div className="panel">
    <div className="panel-heading">
      <div>
        <Button startIcon={<BackIcon />} className={classes.backButton} size="small" onClick={handleClose}>Back to Content Groups</Button>
        <h4>
          {contentGroup.Group.groupID ? contentGroup.Group.groupName : 'New Role'} Details {hasPermission('account_contentGroups.update') ? '' : '(Read Only)'}
        </h4>
      </div>
    </div>
    <div className="panel-body">
      <Tabs
        className={classes.tabs}
        value={selectedTab}
        onChange={(ev, value) => {
          setSelectedTab(value)
          queryString.set({ ...queryString.parse(location.search), tab: value })
        }}
      >
        <Tab label="Settings" />
        <Tab label="Users" />
      </Tabs>
      {renderTab}
    </div>
  </div>
}

const mapStateToProps = (state, ownProps) => {
  const contentGroupsPending = state.data.pending
  const { categoriesLoaded, categoriesPending } = ownProps
  const pending = contentGroupsPending || categoriesPending
  // for content groups being created contentGroup will be passed in ownProps
  const contentGroup = ownProps.contentGroup || (state.data.contentGroups && state.data.contentGroups.find(group => group.Group.groupID == ownProps.id))
  const createPending = getHasRequestPending(state, CREATE_CONTENT_GROUP)
  return { contentGroup, pending, categoriesLoaded, createPending }
}

export default connect(mapStateToProps)(GroupDetails)
